import { motion } from "framer-motion";
import CountUp from "react-countup";
import {
  ArrowRight,
  Rocket,
  Users,
  Star,
  Sparkles,
} from "lucide-react";

import about from "../image/about.jpeg";

const stats = [
  {
    id: 1,
    icon: Rocket,
    value: 50,
    suffix: "+",
    title: "Projects Delivered",
    color: "from-indigo-500 to-blue-500",
  },
  {
    id: 2,
    icon: Users,
    value: 30,
    suffix: "+",
    title: "Happy Clients",
    color: "from-purple-500 to-pink-500",
  },
  {
    id: 3,
    icon: Star,
    value: 5,
    suffix: "★",
    title: "Client Rating",
    color: "from-yellow-400 to-orange-500",
  },
];

const containerVariants = {
  hidden: {},

  show: {
    transition: {
      staggerChildren: 0.18,
    },
  },
};

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 40,
    filter: "blur(10px)",
  },

  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",

    transition: {
      duration: 0.7,
      ease: "easeOut",
    },
  },
};

function About() {
  return (
    <section
    id="about"
    className="relative overflow-hidden py-10 sm:py-14 md:py-16 bg-[radial-gradient(circle_at_top_left,_#f5f3ff,_#ffffff_40%,_#eef2ff)]"
  >
      {/* ================= Background ================= */}

      <motion.div
        animate={{
          x: [0, 80, -20, 0],
          y: [0, -30, 20, 0],
        }}
        transition={{
          duration: 18,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-purple-300/30 blur-[110px]"
      />

      <motion.div
        animate={{
          x: [0, -60, 20, 0],
          y: [0, 30, -20, 0],
        }}
        transition={{
          duration: 22,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute right-0 top-1/3 w-96 h-96 rounded-full bg-indigo-300/30 blur-[120px]"
      />

      <motion.div
        animate={{
          scale: [1, 1.15, 1],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
        }}
        className="absolute bottom-0 left-1/2 -translate-x-1/2
        w-[450px] aspect-square
        rounded-full bg-pink-300/20 blur-[150px]"
      />

      {/* ================= Grid ================= */}

      <div className="relative max-w-6xl mx-auto px-4 md:px-6 flex flex-col lg:flex-row items-center gap-8 sm:gap-10 md:gap-14 lg:gap-16">
                {/* ================= LEFT CONTENT ================= */}

                <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.25 }}
          className="flex-1"
        >
          {/* Badge */}

          <motion.div
            variants={itemVariants}
         className="inline-flex items-center gap-1.5 px-3 py-1 md:px-4 md:py-2 rounded-full bg-white/80 backdrop-blur-xl border border-purple-200 shadow-lg"
          >
           <Sparkles className="w-3.5 h-3.5 md:w-4 md:h-4 text-purple-600" />

           <span className="text-[11px] md:text-sm font-semibold text-purple-700">
              About GrowX
            </span>
          </motion.div>

          {/* Heading */}

          <motion.h2
            variants={itemVariants}
            className="mt-4 sm:mt-6 text-2xl sm:text-4xl md:text-5xl font-black leading-tight"
          >
            We Create

            <span className="block mt-2 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              Digital Experiences
            </span>

            That Grow Brands.
          </motion.h2>

          {/* Line */}

          <motion.div
            variants={itemVariants}
            className="mt-4 md:mt-6 w-16 sm:w-20 md:w-28 h-1 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600"
          />

          {/* Description */}

          <motion.p
            variants={itemVariants}
          className="mt-4 sm:mt-6 text-sm sm:text-base md:text-lg text-gray-600 leading-6 sm:leading-7 md:leading-8"
          >
            GrowX is a full-service digital agency dedicated to helping
            startups and businesses build powerful digital products that make a
            lasting impact.
          </motion.p>

          <motion.p
            variants={itemVariants}
          className="mt-3 sm:mt-4 text-sm sm:text-base md:text-lg text-gray-500 leading-6 sm:leading-7 md:leading-8"
          >
            From AI solutions and web development to mobile apps, branding and
            digital marketing, we combine creativity with cutting-edge
            technology to deliver measurable business growth.
          </motion.p>

          {/* CTA */}

          <motion.div
            variants={itemVariants}
           className="mt-6 sm:mt-8 flex flex-wrap gap-2 sm:gap-5"
          >
            <motion.a
              href="#contact"
              whileHover={{
                scale: 1.05,
              }}
              whileTap={{
                scale: 0.96,
              }}
             className="group relative overflow-hidden inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 px-4 sm:px-6 md:px-7 py-2.5 sm:py-3 md:py-4 text-sm font-semibold text-white shadow-xl"
            >
              <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 group-hover:translate-x-full"></span>

              <span className="relative z-10">
                Let's Talk
              </span>

              <ArrowRight
                size={18}
                className="relative z-10 transition-transform duration-300 group-hover:translate-x-1"
              />
            </motion.a>

            <motion.a
              href="#services"
              whileHover={{
                y: -3,
              }}
             className="rounded-xl border border-purple-200 bg-white/80 backdrop-blur-xl px-4 sm:px-6 md:px-7 py-2.5 sm:py-3 md:py-4 text-sm font-semibold text-purple-700 shadow-lg hover:border-purple-400"
            >
              Explore Services
            </motion.a>
          </motion.div>

          {/* Stats */}

          <motion.div
            variants={itemVariants}
           className="grid grid-cols-3 gap-2 sm:gap-4 md:gap-5 mt-8 sm:mt-10 md:mt-14"
          >
            {stats.map((item) => {
              const Icon = item.icon;

              return (
                <motion.div
                  key={item.id}
                  whileHover={{
                    y: -12,
                    scale: 1.05,
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 260,
                  }}
               className="group rounded-2xl sm:rounded-3xl border border-white bg-white/70 backdrop-blur-xl p-2 sm:p-3 shadow-xl"
                >
                 <div className="flex items-center gap-3">
                 <div
                      className={`flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-lg sm:rounded-xl bg-gradient-to-r ${item.color} text-white shadow-lg`}
                    >
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                    </div>

                    <h3 className="text-lg sm:text-2xl md:text-3xl font-black text-gray-800">
                        <CountUp
                          end={item.value}
                          duration={2.5}
                          enableScrollSpy
                          scrollSpyOnce
                        />
                        {item.suffix}
                      </h3>
                                      </div>

                 <p className="mt-2 sm:mt-4 text-[10px] sm:text-xs md:text-sm text-center text-gray-500 leading-4 sm:leading-5">
                    {item.title}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        </motion.div>

        {/* ================= IMAGE ================= */}

        <motion.div
          initial={{
            opacity: 0,
            x: 80,
            scale: 0.9,
          }}
          whileInView={{
            opacity: 1,
            x: 0,
            scale: 1,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.9,
          }}
          animate={{
            y: [0, -12, 0],
          }}
          className="relative flex-1"
        >
          {/* Glow */}

          <div className="absolute -left-8 -top-8 h-36 w-36 rounded-full bg-purple-400/30 blur-3xl"></div>

          <div className="absolute -bottom-8 -right-8 h-40 w-40 rounded-full bg-indigo-400/30 blur-3xl"></div>

          {/* Border */}

          <motion.div
            whileHover={{
              rotateX: -4,
              rotateY: 5,
              scale: 1.03,
            }}
            transition={{
              type: "spring",
              stiffness: 200,
            }}
            className="rounded-[34px] bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-[2px] shadow-2xl"
            style={{
              transformStyle: "preserve-3d",
            }}
          >
            <div className="overflow-hidden rounded-[32px] bg-white">
              <motion.img
                whileHover={{
                  scale: 1.08,
                }}
                transition={{
                  duration: 0.6,
                }}
                src={about}
                alt="GrowX Team"
                className="h-[320px] sm:h-[450px] md:h-[580px] w-full object-cover"
              />

              <div className="absolute inset-0 rounded-[32px] bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
            </div>
          </motion.div>
        </motion.div>

      </div>

    </section>
  );
}

export default About;